/**
 * SOIRÉES DE BILLETTERIE LIVE CLUB — le modèle pur des écrans (22/10/2026).
 *
 * Contrat `soirees-v1` (billetterie, docs/GESTION-SOIREES-v1.md, §6.5) :
 *  · la billetterie est PROPRIÉTAIRE des soirées, des tarifs et de ses règles.
 *    Elle dit elle-même ce qu'elle refuserait (`gestes`), ce qui ne se change
 *    plus (`verrous`) et ce qui est enregistré sans effet (`avertissements`) ;
 *  · Gestion ajoute ses DROITS (`_actions`) et son état d'ÉCRITURE (gel, Mode
 *    sûr, raccordement : `ecriture`). Un bouton n'est actif que si les trois
 *    le permettent ; sinon, la PREMIÈRE raison, dans l'ordre : droits,
 *    écriture, gestes (§6.5) ;
 *  · l'écran ne calcule JAMAIS un instant : il affiche ceux qu'il a lus (à
 *    Paris), renvoie tels quels les `_paris` et `_heure` lus, et ne fait que de
 *    l'arithmétique de calendrier (nuit + 1 jour).
 *
 * Tout ici est testable sans navigateur (tests/billetterie-soirees-web.test.ts).
 */

import { formatMoney } from '../finance/money.js'

/* ═══════════════════════ les mots de la billetterie ═══════════════════════ */

export const NOM_BILLETTERIE = 'Billetterie Live Club'

export type StatutSoiree = 'BROUILLON' | 'PROGRAMMEE' | 'EN_VENTE' | 'CLOTUREE' | 'ANNULEE'
export type Canal = 'EN_LIGNE' | 'SUR_PLACE' | 'INVITATION'

export const STATUTS: readonly StatutSoiree[] = ['BROUILLON', 'PROGRAMMEE', 'EN_VENTE', 'CLOTUREE', 'ANNULEE']
export const CANAUX: readonly Canal[] = ['SUR_PLACE', 'EN_LIGNE', 'INVITATION']

/** Libellés de la page de secours (`secours/pages.ts`) : les mêmes mots des deux côtés. */
export const STATUT_LIBELLE: Record<StatutSoiree, string> = {
  BROUILLON: 'Brouillon', PROGRAMMEE: 'Programmée', EN_VENTE: 'En vente', CLOTUREE: 'Clôturée', ANNULEE: 'Annulée',
}
export const CANAL_LIBELLE: Record<Canal, string> = { EN_LIGNE: 'En ligne', SUR_PLACE: 'Sur place', INVITATION: 'Invitation' }

export type Ton = 'ok' | 'warning' | 'error' | 'idle'

/** La pastille d'une soirée : la couleur ne parle jamais seule (texte + point). */
export function tonStatut(s: StatutSoiree): Ton {
  return s === 'EN_VENTE' ? 'ok' : s === 'PROGRAMMEE' ? 'warning' : s === 'ANNULEE' ? 'error' : 'idle'
}

/** Ce qui se passe VRAIMENT à la porte (`vente_reelle`), jamais la règle seule du tarif. */
export function tonVente(code: string): Ton {
  if (code === 'OUVERT') return 'ok'
  if (code === 'EPUISE' || code === 'JAUGE' || code === 'COMPLET') return 'warning'
  return 'idle'
}

export const RAISON_DISPO_LIBELLE: Record<string, string> = {
  OUVERT: 'En vente', RETIRE: 'Retiré', PAS_ENCORE: 'Pas encore ouvert', TERMINE: 'Vente terminée',
  EPUISE: 'Épuisé', EN_ATTENTE: 'Après le tarif précédent', JAUGE: 'Soirée complète',
}

/** Ce qui ne se change plus, dit à côté du champ grisé (§4.3). */
export const VERROU_LIBELLE: Record<string, string> = {
  NUIT: 'La date ne change plus : un billet est déjà vendu.',
  SALLES_RETRAIT: 'Aucune salle ne se retire plus : un billet est déjà vendu. On peut en ajouter.',
  HORAIRES_RESSERRER: 'Un billet est déjà vendu : on peut ouvrir plus tôt ou fermer plus tard, pas l’inverse.',
  PRIX: 'Le prix est figé depuis le premier billet émis : pour vendre moins cher, créez un nouveau tarif.',
  FRAIS: 'Les frais sont figés depuis le premier billet émis.',
  CANAL: 'Le canal est figé depuis le premier billet émis.',
  EXONERATION: 'L’exonération est figée depuis le premier billet émis.',
  TVA: 'La TVA est figée depuis le premier billet émis.',
  ENTREE_AVANT_AVANCER: 'L’heure limite d’entrée peut être repoussée ou supprimée, jamais avancée ni ajoutée : un billet est déjà émis.',
}

/** Phrase imposée (§5.4) : le gel n'arrête QUE les gestes faits depuis Gestion. */
export const PHRASE_GEL = 'Geler arrête les modifications faites depuis Gestion. Une mise en vente déjà programmée part quand même à son heure : pour l’empêcher, remettez la soirée en brouillon avant de geler.'

/** §6.4, étape 6 : un brouillon passé en vente entre la lecture et le clic. */
export const PHRASE_PASSEE_EN_LIGNE = 'La soirée vient de passer en vente : rechargez-la avant de recommencer.'

export const PHRASE_IDENTITE_ANNULEE = 'Confirmation d’identité annulée : rien n’a été fait.'

export const PHRASE_DEMO = 'Démo · ne compte nulle part : ni calendrier, ni compta, ni Accueil.'

/* ═══════════════════════ lecture défensive ═══════════════════════
   Le serveur de Gestion relit déjà la billetterie champ par champ (§6.5).
   Ici, on ne fait confiance à rien de plus : un champ obligatoire illisible
   rend la réponse inutilisable plutôt qu'à moitié fausse. */

type Obj = Record<string, unknown>
const obj = (v: unknown): Obj | null => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null)
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const strOuNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const ent = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) ? v : null)
const bool = (v: unknown, defaut = false): boolean => (typeof v === 'boolean' ? v : defaut)
const liste = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])

/**
 * L'AFFICHE D'UNE SOIRÉE (§9.3) : ce que la billetterie en dit, jamais les
 * octets — l'image se lit par sa propre adresse (`urlAffiche`). Une affiche
 * illisible vaut « pas d'affiche » : elle n'est qu'un décor, la soirée reste
 * affichée (le repli s'y substitue). `version` est celle à renvoyer en
 * `version_affiche` pour la changer ou la retirer.
 */
export interface Affiche { empreinte: string; largeur: number; hauteur: number; teinte: string; version: number }

export const EMPREINTE_RX = /^[0-9a-f]{64}$/
const TEINTE_RX = /^#[0-9a-f]{6}$/i

export function lireAffiche(v: unknown): Affiche | null {
  const o = obj(v)
  if (!o) return null
  const empreinte = str(o['empreinte']); const largeur = ent(o['largeur']); const hauteur = ent(o['hauteur'])
  const teinte = str(o['teinte']); const version = ent(o['version'])
  if (!empreinte || !EMPREINTE_RX.test(empreinte) || !teinte || !TEINTE_RX.test(teinte)) return null
  if (largeur === null || largeur < 1 || hauteur === null || hauteur < 1 || version === null || version < 1) return null
  return { empreinte, largeur, hauteur, teinte: teinte.toLowerCase(), version }
}

export interface Avertissement { code: string; message: string }
export interface Effet { tarif_id: string; nom: string; prix_cents: number | null; avant: string; apres: string }

export interface Geste {
  possible: boolean
  raison: string | null
  /** Soirée en ligne : ce geste demande le droit de mise en vente et une confirmation d'identité (§2.3). */
  publier_requis: boolean
  verrous: string[]
  avertissements: Avertissement[]
  effets: Effet[]
  jauge_min: number | null
  quota_min: number | null
}

const GESTE_ABSENT: Geste = {
  possible: false, raison: 'La billetterie n’a pas dit si ce geste est possible : rechargez la soirée.',
  publier_requis: false, verrous: [], avertissements: [], effets: [], jauge_min: null, quota_min: null,
}

function lireAvertissements(v: unknown): Avertissement[] {
  const out: Avertissement[] = []
  for (const a of liste(v)) {
    const o = obj(a)
    const code = str(o?.['code'])
    const message = str(o?.['message'])
    if (code && message) out.push({ code, message })
  }
  return out
}

function lireEffets(v: unknown): Effet[] {
  const out: Effet[] = []
  for (const e of liste(v)) {
    const o = obj(e)
    const id = str(o?.['tarif_id']); const nom = str(o?.['nom'])
    const avant = str(o?.['avant']); const apres = str(o?.['apres'])
    if (id && nom && avant && apres) out.push({ tarif_id: id, nom, prix_cents: ent(o?.['prix_cents']), avant, apres })
  }
  return out
}

export function lireGeste(v: unknown): Geste {
  const o = obj(v)
  if (!o || typeof o['possible'] !== 'boolean') return GESTE_ABSENT
  return {
    possible: o['possible'],
    raison: strOuNull(o['raison']),
    publier_requis: bool(o['publier_requis']),
    verrous: liste(o['verrous']).filter((x): x is string => typeof x === 'string'),
    avertissements: lireAvertissements(o['avertissements']),
    effets: lireEffets(o['effets']),
    jauge_min: ent(o['jauge_min']),
    quota_min: ent(o['quota_min']),
  }
}

export interface Ecriture { possible: boolean; cause: 'GEL' | 'MODE_SUR' | 'NON_RACCORDEE' | null; message: string | null }
export interface Actions { preparer: boolean; publier: boolean; regler: boolean; voir_recette: boolean }

export function lireEcriture(v: unknown): Ecriture {
  const o = obj(v)
  if (!o) return { possible: false, cause: null, message: 'Gestion n’a pas dit si les modifications sont possibles : rechargez la page.' }
  const c = str(o['cause'])
  const cause = c === 'GEL' || c === 'MODE_SUR' || c === 'NON_RACCORDEE' ? c : null
  return { possible: bool(o['possible']), cause, message: strOuNull(o['message']) }
}

export function lireActions(v: unknown): Actions {
  const o = obj(v)
  return { preparer: bool(o?.['preparer']), publier: bool(o?.['publier']), regler: bool(o?.['regler']), voir_recette: bool(o?.['voir_recette']) }
}

export interface Tarif {
  id: string; nom: string; description: string | null; canal: Canal
  prix_cents: number; frais_service_cents: number; tva_bp: number | null; quota: number | null
  cascade_groupe: string | null; cascade_rang: number | null
  vente_debut: string | null; vente_debut_paris: string | null
  vente_fin: string | null; vente_fin_paris: string | null
  entree_avant: string | null; entree_avant_heure: string | null
  salles: string[] | null; exoneration: boolean; cache: boolean; actif: boolean; ordre: number
  consigne_porte: string | null; version: number
  compteurs: { vendus: number; entres: number; emis: number }
  dispo: { disponible: boolean; raison: string; libelle: string; restant: number | null }
  vente_reelle: { code: string; libelle: string }
  gestes: { modifier: Geste; retirer: Geste; remettre: Geste; supprimer: Geste }
  avertissements: Avertissement[]
}

const estCanal = (v: unknown): v is Canal => v === 'EN_LIGNE' || v === 'SUR_PLACE' || v === 'INVITATION'
const estStatut = (v: unknown): v is StatutSoiree => typeof v === 'string' && (STATUTS as readonly string[]).includes(v)

export function lireTarif(v: unknown): Tarif | null {
  const o = obj(v)
  if (!o) return null
  const id = str(o['id']); const nom = str(o['nom']); const prix = ent(o['prix_cents']); const version = ent(o['version'])
  if (!id || nom === null || !estCanal(o['canal']) || prix === null || version === null) return null
  const c = obj(o['compteurs']); const d = obj(o['dispo']); const vr = obj(o['vente_reelle']); const g = obj(o['gestes'])
  const sallesBrutes = o['salles']
  return {
    id, nom, description: strOuNull(o['description']), canal: o['canal'],
    prix_cents: prix, frais_service_cents: ent(o['frais_service_cents']) ?? 0, tva_bp: ent(o['tva_bp']), quota: ent(o['quota']),
    cascade_groupe: strOuNull(o['cascade_groupe']), cascade_rang: ent(o['cascade_rang']),
    vente_debut: strOuNull(o['vente_debut']), vente_debut_paris: strOuNull(o['vente_debut_paris']),
    vente_fin: strOuNull(o['vente_fin']), vente_fin_paris: strOuNull(o['vente_fin_paris']),
    entree_avant: strOuNull(o['entree_avant']), entree_avant_heure: strOuNull(o['entree_avant_heure']),
    salles: Array.isArray(sallesBrutes) ? sallesBrutes.filter((x): x is string => typeof x === 'string') : null,
    exoneration: bool(o['exoneration']), cache: bool(o['cache']), actif: bool(o['actif'], true), ordre: ent(o['ordre']) ?? 0,
    consigne_porte: strOuNull(o['consigne_porte']), version,
    compteurs: { vendus: ent(c?.['vendus']) ?? 0, entres: ent(c?.['entres']) ?? 0, emis: ent(c?.['emis']) ?? 0 },
    dispo: {
      disponible: bool(d?.['disponible']), raison: str(d?.['raison']) ?? '',
      libelle: str(d?.['libelle']) ?? RAISON_DISPO_LIBELLE[str(d?.['raison']) ?? ''] ?? '—', restant: ent(d?.['restant']),
    },
    // §3.1 : l'écran affiche `vente_reelle.libelle`, jamais `dispo.libelle` seul.
    vente_reelle: { code: str(vr?.['code']) ?? 'INCONNU', libelle: str(vr?.['libelle']) ?? 'État de vente inconnu' },
    gestes: {
      modifier: lireGeste(g?.['modifier']), retirer: lireGeste(g?.['retirer']),
      remettre: lireGeste(g?.['remettre']), supprimer: lireGeste(g?.['supprimer']),
    },
    avertissements: lireAvertissements(o['avertissements']),
  }
}

export interface SalleSoiree { code: string; nom: string; jauge: number | null }
export interface Recette { especes_cents: number; cb_cents: number; en_ligne_cents: number; total_cents: number; billets_gratuits: number }
export interface CaisseOuverte { poste: string; depuis: string | null; telephone: boolean }

export interface GestesSoiree {
  modifier: Geste; publier: Geste; programmer: Geste; brouillon: Geste; annuler: Geste; arreter_ventes: Geste; ajouter_tarif: Geste
}

export interface Soiree {
  id: string; nom: string; nuit: string
  ouverture: string | null; fermeture: string | null; ouverture_heure: string | null; fermeture_heure: string | null
  statut: StatutSoiree
  mise_en_vente_a: string | null; mise_en_vente_paris: string | null; cloture_auto_a: string | null
  jauge_totale: number; famille: string | null; presentation: string | null
  version: number; entrainement: boolean; complet_a: string | null
  /** §9.3 : `null` sans affiche (ou affiche illisible). */
  affiche: Affiche | null
  publiee_a: string | null; annulee_a: string | null; motif_annulation: string | null
  cree_a: string | null; maj_a: string | null
  salles: SalleSoiree[]
  compteurs: { vendus: number; entres: number; places_occupees: number; jauge_restante: number | null }
  recette: Recette | null
  caisses_ouvertes: CaisseOuverte[]
  gestes: GestesSoiree
  avertissements: Avertissement[]
  tarifs: Tarif[]
}

export function lireSoiree(v: unknown): Soiree | null {
  const o = obj(v)
  if (!o) return null
  const id = str(o['id']); const nom = str(o['nom']); const nuit = str(o['nuit']); const version = ent(o['version'])
  if (!id || nom === null || !nuit || !estStatut(o['statut']) || version === null) return null
  const c = obj(o['compteurs']); const r = obj(o['recette']); const g = obj(o['gestes'])
  const tarifs: Tarif[] = []
  for (const t of liste(o['tarifs'])) {
    const lu = lireTarif(t)
    // Un tarif illisible rendrait la fiche fausse en silence : on la refuse entière.
    if (!lu) return null
    tarifs.push(lu)
  }
  tarifs.sort((a, b) => a.ordre - b.ordre)
  const salles: SalleSoiree[] = []
  for (const s of liste(o['salles'])) {
    const x = obj(s); const code = str(x?.['code'])
    if (code) salles.push({ code, nom: str(x?.['nom']) ?? code, jauge: ent(x?.['jauge']) })
  }
  const caisses: CaisseOuverte[] = []
  for (const k of liste(o['caisses_ouvertes'])) {
    const x = obj(k); const poste = str(x?.['poste'])
    if (poste) caisses.push({ poste, depuis: strOuNull(x?.['depuis']), telephone: bool(x?.['telephone']) })
  }
  return {
    id, nom, nuit,
    ouverture: strOuNull(o['ouverture']), fermeture: strOuNull(o['fermeture']),
    ouverture_heure: strOuNull(o['ouverture_heure']), fermeture_heure: strOuNull(o['fermeture_heure']),
    statut: o['statut'],
    mise_en_vente_a: strOuNull(o['mise_en_vente_a']), mise_en_vente_paris: strOuNull(o['mise_en_vente_paris']),
    cloture_auto_a: strOuNull(o['cloture_auto_a']),
    jauge_totale: ent(o['jauge_totale']) ?? 0, famille: strOuNull(o['famille']), presentation: strOuNull(o['presentation']),
    version, entrainement: bool(o['entrainement']), complet_a: strOuNull(o['complet_a']),
    affiche: lireAffiche(o['affiche']),
    publiee_a: strOuNull(o['publiee_a']), annulee_a: strOuNull(o['annulee_a']), motif_annulation: strOuNull(o['motif_annulation']),
    cree_a: strOuNull(o['cree_a']), maj_a: strOuNull(o['maj_a']),
    salles,
    compteurs: {
      vendus: ent(c?.['vendus']) ?? 0, entres: ent(c?.['entres']) ?? 0,
      places_occupees: ent(c?.['places_occupees']) ?? 0, jauge_restante: ent(c?.['jauge_restante']),
    },
    recette: r ? {
      especes_cents: ent(r['especes_cents']) ?? 0, cb_cents: ent(r['cb_cents']) ?? 0, en_ligne_cents: ent(r['en_ligne_cents']) ?? 0,
      total_cents: ent(r['total_cents']) ?? 0, billets_gratuits: ent(r['billets_gratuits']) ?? 0,
    } : null,
    caisses_ouvertes: caisses,
    gestes: {
      modifier: lireGeste(g?.['modifier']), publier: lireGeste(g?.['publier']), programmer: lireGeste(g?.['programmer']),
      brouillon: lireGeste(g?.['brouillon']), annuler: lireGeste(g?.['annuler']),
      arreter_ventes: lireGeste(g?.['arreter_ventes']), ajouter_tarif: lireGeste(g?.['ajouter_tarif']),
    },
    avertissements: lireAvertissements(o['avertissements']),
    tarifs,
  }
}

/**
 * Ce que la billetterie a fait EN PLUS du geste, tel que le serveur le relaie
 * (`resultat`, §4.6 à §4.11) : avertissements (`HEURE_DECALEE_AMBIGUE`…),
 * tarifs décalés avec la nuit, effets réels sur les autres tarifs, tarifs
 * retirés par l'arrêt des ventes, geste sans effet (`inchange`). Ces
 * avertissements n'existent QUE dans la réponse de l'écriture : la fiche
 * relue ne les porte plus.
 */
export interface ResultatGeste {
  avertissements: Avertissement[]
  tarifs_decales: number
  effets: Effet[]
  tarifs_retires: { id: string; nom: string }[]
  inchange: boolean
}

export function lireResultat(v: unknown): ResultatGeste | null {
  const o = obj(v)
  if (!o) return null
  const retires: { id: string; nom: string }[] = []
  for (const t of liste(o['tarifs_retires'])) {
    const x = obj(t); const id = str(x?.['id']); const nom = str(x?.['nom'])
    if (id && nom !== null) retires.push({ id, nom })
  }
  return {
    avertissements: lireAvertissements(o['avertissements']), tarifs_decales: ent(o['tarifs_decales']) ?? 0,
    effets: lireEffets(o['effets']), tarifs_retires: retires, inchange: o['inchange'] === true,
  }
}

/** La fiche, telle que la rend `GET …/soirees/:id` ET chaque écriture (§6.5). */
export interface Fiche {
  /** `null` seulement quand l'écriture a réussi mais que la relecture a échoué (`relecture: 'ECHEC'`). */
  soiree: Soiree | null
  relecture_echec: boolean
  recette_masquee: boolean
  heure_serveur: string | null
  ecriture: Ecriture
  actions: Actions
  rejouee: boolean
  /** Après une écriture seulement : ce que la billetterie a vraiment fait (null pour une lecture). */
  resultat: ResultatGeste | null
}

export function lireFiche(v: unknown): Fiche | null {
  const o = obj(v)
  if (!o) return null
  const relectureEchec = o['relecture'] === 'ECHEC'
  const soiree = lireSoiree(o['soiree'])
  if (!soiree && !relectureEchec) return null
  return {
    soiree, relecture_echec: relectureEchec && !soiree,
    recette_masquee: bool(o['recette_masquee']), heure_serveur: strOuNull(o['heure_serveur']),
    ecriture: lireEcriture(o['ecriture']), actions: lireActions(o['_actions']), rejouee: bool(o['rejouee']),
    resultat: lireResultat(o['resultat']),
  }
}

export interface SoireeListe {
  id: string; nom: string; nuit: string
  ouverture_heure: string | null; fermeture_heure: string | null
  statut: StatutSoiree; mise_en_vente_a: string | null
  jauge_totale: number | null; vendus: number; entres: number; entrainement: boolean; version: number; maj_a: string | null
  affiche: Affiche | null
}

export interface ReponseListe {
  nom_affiche: string
  demo: { active: boolean; message: string }
  soirees: SoireeListe[]
  tronque: boolean
  nuit_courante: string | null
  nuit_proposee: string | null
  heure_serveur: string | null
  ecriture: Ecriture
  actions: Actions
}

export function lireListe(v: unknown): ReponseListe | null {
  const o = obj(v)
  if (!o || !Array.isArray(o['soirees'])) return null
  const soirees: SoireeListe[] = []
  for (const s of o['soirees'] as unknown[]) {
    const x = obj(s)
    const id = str(x?.['id']); const nom = str(x?.['nom']); const nuit = str(x?.['nuit']); const version = ent(x?.['version'])
    if (!x || !id || nom === null || !nuit || !estStatut(x['statut']) || version === null) continue
    soirees.push({
      id, nom, nuit,
      ouverture_heure: strOuNull(x['ouverture_heure']), fermeture_heure: strOuNull(x['fermeture_heure']),
      statut: x['statut'], mise_en_vente_a: strOuNull(x['mise_en_vente_a']),
      jauge_totale: ent(x['jauge_totale']), vendus: ent(x['vendus']) ?? 0, entres: ent(x['entres']) ?? 0,
      entrainement: bool(x['entrainement']), version, maj_a: strOuNull(x['maj_a']),
      affiche: lireAffiche(x['affiche']),
    })
  }
  const demo = obj(o['demo'])
  return {
    nom_affiche: str(o['nom_affiche']) ?? NOM_BILLETTERIE,
    demo: { active: bool(demo?.['active'], true), message: str(demo?.['message']) ?? PHRASE_DEMO },
    soirees, tronque: bool(o['tronque']),
    nuit_courante: strOuNull(o['nuit_courante']), nuit_proposee: strOuNull(o['nuit_proposee']),
    heure_serveur: strOuNull(o['heure_serveur']),
    ecriture: lireEcriture(o['ecriture']), actions: lireActions(o['_actions']),
  }
}

export interface Salle { code: string; nom: string; jauge_defaut: number | null; ordre: number; active: boolean }

export function lireSalles(v: unknown): Salle[] | null {
  const o = obj(v)
  if (!o || !Array.isArray(o['salles'])) return null
  const out: Salle[] = []
  for (const s of o['salles'] as unknown[]) {
    const x = obj(s); const code = str(x?.['code'])
    if (x && code) out.push({ code, nom: str(x['nom']) ?? code, jauge_defaut: ent(x['jauge_defaut']), ordre: ent(x['ordre']) ?? 0, active: bool(x['active'], true) })
  }
  return out.sort((a, b) => a.ordre - b.ordre)
}

/* ═══════════════════════ les erreurs ═══════════════════════ */

export type GenreErreur =
  | 'reseau' | 'indisponible' | 'coupee' | 'bloquee' | 'interdit' | 'identite' | 'gel' | 'mode_sur'
  | 'version' | 'heure_ambigue' | 'heure_inexistante' | 'validation' | 'introuvable' | 'refus' | 'autre'

export interface Ambiguite { champ: string; possibles: string[] }

export interface ErreurBM {
  genre: GenreErreur
  code: string
  status: number
  /** Le message de la billetterie ou de Gestion, écrit pour être lu : affiché tel quel (§3.9). */
  message: string
  champs: string[]
  version: number | null
  ambiguites: Ambiguite[]
  traceId: string | null
}

const INDISPONIBLES = new Set([
  'BILLETTERIE_UNAVAILABLE', 'BILLETTERIE_NOT_CONFIGURED', 'BILLETTERIE_TOKEN_REFUSED',
  'BILLETTERIE_DISABLED', 'BILLETTERIE_INVALID_RESPONSE',
])

function genreDe(status: number, code: string): GenreErreur {
  if (code === 'BILLETTERIE_SOIREES_GESTION_COUPEES') return 'coupee'
  if (code === 'BILLETTERIE_COMPTE_BLOQUE_ICI') return 'bloquee'
  if (code === 'STEPUP_REQUIRED') return 'identite'
  if (code === 'BILLETTERIE_ECRITURES_GELEES') return 'gel'
  if (code === 'SAFE_MODE') return 'mode_sur'
  if (code === 'BILLETTERIE_VERSION_PERIMEE') return 'version'
  if (code === 'BILLETTERIE_HEURE_AMBIGUE') return 'heure_ambigue'
  if (code === 'BILLETTERIE_HEURE_INEXISTANTE') return 'heure_inexistante'
  if (code === 'BILLETTERIE_INVALIDE' || code === 'VALIDATION_ERROR' || code === 'BILLETTERIE_REQUETE_TROP_GROSSE') return 'validation'
  if (INDISPONIBLES.has(code) || status >= 500) return 'indisponible'
  if (status === 404) return 'introuvable'
  if (status === 403) return 'interdit'
  if (status === 409 || status === 400) return 'refus'
  return 'autre'
}

const MESSAGE_REPLI: Partial<Record<GenreErreur, string>> = {
  indisponible: `${NOM_BILLETTERIE} ne répond pas pour le moment : rien n’a été modifié. Réessayez dans un instant.`,
  interdit: 'Vous n’avez pas le droit de faire ce geste.',
  identite: PHRASE_IDENTITE_ANNULEE,
  introuvable: `Cette soirée n’existe pas (ou plus) dans ${NOM_BILLETTERIE}.`,
  version: 'La soirée a été modifiée entre-temps. Rechargez la page pour voir la dernière version.',
}

/** Une réponse en erreur, lue champ par champ ; `null` pour une réponse 2xx. */
export function lireErreur(status: number, corps: unknown): ErreurBM | null {
  if (status >= 200 && status < 300) return null
  const o = obj(corps)
  const code = str(o?.['error']) ?? str(o?.['code']) ?? (status >= 500 ? 'BILLETTERIE_UNAVAILABLE' : 'ERREUR')
  const genre = genreDe(status, code)
  const d = obj(o?.['details'])
  const champs = [
    ...liste(d?.['champs']), ...liste(o?.['fields']).map((f) => (typeof f === 'string' ? f : str(obj(f)?.['field']))),
  ].filter((x): x is string => typeof x === 'string' && x !== '')
  const ambiguites: Ambiguite[] = []
  for (const a of liste(d?.['ambiguites'])) {
    const x = obj(a); const champ = str(x?.['champ'])
    const possibles = liste(x?.['possibles']).filter((p): p is string => typeof p === 'string')
    if (champ && possibles.length >= 2) ambiguites.push({ champ, possibles })
  }
  // Un `STEPUP_REQUIRED` qui revient d'`adminMutate` : le dialogue a été fermé sans confirmer.
  const message = genre === 'identite' ? PHRASE_IDENTITE_ANNULEE
    : strOuNull(o?.['message']) ?? MESSAGE_REPLI[genre] ?? 'La demande a échoué : rien n’a été modifié.'
  return { genre, code, status, message, champs, version: ent(d?.['version']), ambiguites, traceId: strOuNull(o?.['traceId']) }
}

export function erreurReseau(ecriture: boolean): ErreurBM {
  return {
    genre: 'reseau', code: 'NETWORK', status: 0,
    message: ecriture
      ? 'Gestion n’a pas pu joindre son serveur : la modification a pu partir ou non. Rechargez la soirée pour le vérifier ; recommencer le même geste, sans rien changer, ne le fera pas deux fois.'
      : 'Gestion n’a pas pu joindre son serveur. Vérifiez la connexion puis réessayez.',
    champs: [], version: null, ambiguites: [], traceId: null,
  }
}

/** Seul `VERSION_PERIMEE` propose « Recharger » (§6.1). */
export function proposeRecharger(e: ErreurBM | null): boolean {
  return e?.genre === 'version'
}

/**
 * Une RELECTURE en échec garde-t-elle l'écran déjà affiché ? Seulement pour une
 * panne passagère (billetterie arrêtée, réseau) : elle se relit seule. Une
 * coupure par la billetterie, un blocage ou un refus vident l'écran — ses
 * boutons échoueraient tous, et la raison doit se voir (§6.5, M14).
 */
export function garderApresErreur(e: ErreurBM): boolean {
  return e.genre === 'indisponible' || e.genre === 'reseau'
}

/** Ce qui se passe ensuite, après une coupure par la billetterie ou un blocage ; null sinon. */
export function suiteErreurAcces(e: ErreurBM): string | null {
  if (e.genre === 'coupee') return 'Rien n’est lu ni modifié tant que la billetterie n’a pas rétabli l’accès de Gestion. La porte et LC Porte continuent.'
  if (e.genre === 'bloquee') return 'Seul un responsable de la billetterie peut lever ce blocage, depuis sa page de secours.'
  return null
}

/* ═══════════════════════ formats (Paris) ═══════════════════════ */

export const euros = (cents: number | null | undefined): string => formatMoney(cents)

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

/** Une date `AAAA-MM-JJ` réelle, à midi UTC ; `null` pour « 2026-02-30 ». */
export function jourCivil(ymd: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null
  const d = new Date(`${ymd}T12:00:00Z`)
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== ymd ? null : d
}

/** Arithmétique de CALENDRIER seulement (principe 5) : la nuit + n jours. */
export function plusJours(ymd: string, n: number): string {
  const d = jourCivil(ymd)
  if (!d) return ymd
  return new Date(d.getTime() + n * 86_400_000).toISOString().slice(0, 10)
}

const quantieme = (d: Date): string => (d.getUTCDate() === 1 ? '1er' : String(d.getUTCDate()))

/**
 * « nuit du mardi 27 au mercredi 28 octobre » (§4.5) ; le mois et l'année ne
 * se répètent que s'ils changent : « nuit du samedi 31 octobre au dimanche
 * 1er novembre », « … du jeudi 31 décembre 2026 au vendredi 1er janvier 2027 ».
 */
export function nuitEnLettres(nuit: string): string {
  const a = jourCivil(nuit)
  if (!a) return nuit
  const b = new Date(a.getTime() + 86_400_000)
  const memeMois = a.getUTCMonth() === b.getUTCMonth()
  const memeAnnee = a.getUTCFullYear() === b.getUTCFullYear()
  const gauche = `${JOURS[a.getUTCDay()]} ${quantieme(a)}${memeMois ? '' : ` ${MOIS[a.getUTCMonth()]}`}${memeAnnee ? '' : ` ${a.getUTCFullYear()}`}`
  const droite = `${JOURS[b.getUTCDay()]} ${quantieme(b)} ${MOIS[b.getUTCMonth()]}${memeAnnee ? '' : ` ${b.getUTCFullYear()}`}`
  return `nuit du ${gauche} au ${droite}`
}

/** « mar. 27/10 » — la nuit en court, pour les listes. */
export function nuitCourte(nuit: string): string {
  const d = jourCivil(nuit)
  if (!d) return nuit
  return `${JOURS[d.getUTCDay()]!.slice(0, 3)}. ${nuit.slice(8, 10)}/${nuit.slice(5, 7)}`
}

const FMT_HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
const FMT_JOUR_HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

/** « 26/10 à 12:00 », heure de Paris — un instant LU, seulement affiché (§3.2). */
export function instantParis(iso: string | null): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '—'
  const p: Record<string, string> = {}
  for (const x of FMT_JOUR_HEURE.formatToParts(t)) p[x.type] = x.value
  return `${p['day']}/${p['month']} à ${p['hour']}:${p['minute']}`
}

export function heureParis(iso: string | null): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  return Number.isNaN(t) ? '—' : FMT_HEURE.format(t)
}

/**
 * La saison d'une heure de Paris relue, seulement quand elle compte : dans
 * l'heure qui existe deux fois (§3.4). La forme à la minute ne porte un
 * décalage que là ; la forme à la seconde (« …T02:30:12.000+01:00 », un
 * instant qui porte des secondes) en porte toujours un : on ne le dit alors
 * que le dernier dimanche d'octobre, entre 02:00 et 02:59 (calendrier seul).
 */
export function saisonDeParis(paris: string): 'hiver' | 'été' | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):\d{2}(:\d{2}(?:\.\d{1,3})?)?([+-]\d{2}:\d{2})$/.exec(paris)
  if (!m) return null
  const saison = m[6] === '+01:00' ? 'hiver' : m[6] === '+02:00' ? 'été' : null
  if (!saison || !m[5]) return saison
  return `${m[1]}-${m[2]}-${m[3]}` === dernierDimanche(Number(m[1]), 9) && m[4] === '02' ? saison : null
}

/**
 * Une heure de Paris telle que la billetterie la rend (`vente_fin_paris`…) :
 * « 28/10/2026 à 02:30 », et « (hiver) » ou « (été) » quand elle est ambiguë
 * (`…+01:00` / `…+02:00`, §3.4). La forme à la seconde s'affiche à la minute.
 */
export function parisLisible(paris: string | null): string {
  if (!paris) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d{1,3})?)?([+-]\d{2}:\d{2})?$/.exec(paris)
  if (!m) return paris
  const saison = saisonDeParis(paris)
  return `${m[3]}/${m[2]}/${m[1]} à ${m[4]}${saison ? ` (${saison})` : ''}`
}

const FMT_MUR_PARIS = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

/**
 * « 2026-10-26T14:05 » : l'heure de Paris de maintenant, au mur — pour dire
 * si une heure de vente SAISIE est déjà passée (affichage seulement : rien
 * n'est envoyé ni converti, principe 5).
 */
export function maintenantParis(t: number = Date.now()): string {
  const p: Record<string, string> = {}
  for (const x of FMT_MUR_PARIS.formatToParts(t)) p[x.type] = x.value
  return `${p['year']}-${p['month']}-${p['day']}T${p['hour']}:${p['minute']}`
}

/** Le dernier dimanche d'un mois (changement d'heure européen), en `AAAA-MM-JJ`. */
function dernierDimanche(annee: number, mois0: number): string {
  const dernier = new Date(Date.UTC(annee, mois0 + 1, 0, 12))
  return new Date(dernier.getTime() - dernier.getUTCDay() * 86_400_000).toISOString().slice(0, 10)
}

/**
 * Une heure « HH:MM » de la nuit tombe-t-elle dans l'heure qui existe deux fois
 * (la nuit qui finit le dernier dimanche d'octobre, de 02:00 à 02:59) ? La
 * billetterie prend alors la SECONDE (heure d'hiver, §3.3) : l'écran peut le
 * dire, sans rien calculer d'autre qu'un calendrier.
 */
export function saisonDeLHeure(nuit: string, hhmm: string): 'hiver' | 'inexistante' | null {
  if (!/^(0[2]):[0-5]\d$/.test(hhmm)) return null
  const lendemain = plusJours(nuit, 1)
  const annee = Number(lendemain.slice(0, 4))
  if (lendemain === dernierDimanche(annee, 9)) return 'hiver'
  if (lendemain === dernierDimanche(annee, 2)) return 'inexistante'
  return null
}

/** « 02:30 (hiver) », ou « 02:30 (n'existe pas : 03:30) » la nuit du printemps. */
export function heureDeNuitLisible(nuit: string, hhmm: string | null): string {
  if (!hhmm) return '—'
  const s = saisonDeLHeure(nuit, hhmm)
  if (s === 'hiver') return `${hhmm} (hiver)`
  if (s === 'inexistante') return `${hhmm} (n’existe pas cette nuit-là : 03:${hhmm.slice(3)})`
  return hhmm
}

/** « 5,5 % » ; `null` = le réglage de la billetterie. */
export function tvaLisible(bp: number | null, defautBp?: number | null): string {
  if (bp === null) {
    return typeof defautBp === 'number' ? `${pourcent(defautBp)} (réglage par défaut)` : 'réglage par défaut'
  }
  return pourcent(bp)
}

function pourcent(bp: number): string {
  return `${(bp / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`
}

/* ═══════════════════════ saisies (texte → valeurs du contrat) ═══════════════════════ */

/**
 * Un nom qui ne reste vide qu'une fois les invisibles retirés (« \u200b »,
 * « \u00ad ») : refusé avant l'envoi, comme le fait le serveur de Gestion —
 * la fiche se lirait « Sans nom ».
 */
export function nomVide(v: string): boolean {
  return v.replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]+/gu, ' ').trim() === ''
}

export const HEURE_RX = /^([01]\d|2[0-3]):[0-5]\d$/
export const PARIS_RX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
/** L'instant exact relu (`…+01:00`) : renvoyé tel quel, jamais réinterprété. */
export const INSTANT_RX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/
export const GROUPE_RX = /^[a-z0-9][a-z0-9-]{0,31}$/
export const SALLE_RX = /^[A-Z0-9][A-Z0-9-]{0,15}$/
export const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** « 8 », « 8,5 », « 8,50 », « 8.50 » → 850 ; vide → `null` ; illisible → `undefined`. */
export function centsDepuisSaisie(v: string): number | null | undefined {
  const t = v.trim().replace(/\s|€/g, '')
  if (t === '') return null
  const m = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(t)
  if (!m) return undefined
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'))
}

/** 850 → « 8,50 » (la saisie, sans le symbole). */
export function saisieDepuisCents(c: number | null): string {
  if (c === null) return ''
  return `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`
}

/** « 5,5 » → 550 ; vide → `null` ; illisible → `undefined`. */
export function bpDepuisSaisie(v: string): number | null | undefined {
  const t = v.trim().replace(/\s|%/g, '')
  if (t === '') return null
  const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(t)
  if (!m) return undefined
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'))
  return bp <= 10_000 ? bp : undefined
}

export function saisieDepuisBp(bp: number | null): string {
  if (bp === null) return ''
  return (bp / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2, useGrouping: false })
}

/** Entier positif d'une saisie ; vide → `null` ; illisible → `undefined`. */
export function entierDepuisSaisie(v: string): number | null | undefined {
  const t = v.trim().replace(/\s/g, '')
  if (t === '') return null
  return /^\d{1,6}$/.test(t) ? Number(t) : undefined
}

/* ═══════════════════════ droits, écriture, gestes ═══════════════════════ */

export const DROIT_PREPARER = 'Préparer les soirées de Billetterie Live Club'
export const DROIT_PUBLIER = 'Mettre en vente les soirées de Billetterie Live Club'

export type NomGeste =
  | 'modifier_soiree' | 'ajouter_tarif' | 'modifier_tarif' | 'remettre_tarif' | 'retirer_tarif' | 'supprimer_tarif'
  | 'publier' | 'programmer' | 'brouillon' | 'annuler' | 'arreter_ventes'

const GESTES_DE_VENTE: readonly NomGeste[] = ['publier', 'programmer', 'brouillon', 'annuler', 'arreter_ventes']
/** Gestes de préparation qui deviennent des gestes de mise en vente sur une soirée en ligne (principe 8). */
const PREPARATION_EN_LIGNE: readonly NomGeste[] = ['modifier_soiree', 'ajouter_tarif', 'modifier_tarif', 'remettre_tarif']

/** Le droit manquant pour ce geste, ou `null` (le serveur reste seul juge). */
export function droitManquant(nom: NomGeste, geste: Geste, a: Actions): string | null {
  if (GESTES_DE_VENTE.includes(nom)) return a.publier ? null : `Il vous faut le droit « ${DROIT_PUBLIER} ».`
  if (!a.preparer) return `Il vous faut le droit « ${DROIT_PREPARER} ».`
  if (PREPARATION_EN_LIGNE.includes(nom) && geste.publier_requis && !a.publier) {
    return `Cette soirée est en ligne : ce changement agit tout de suite sur la vente et demande le droit « ${DROIT_PUBLIER} ».`
  }
  return null
}

export function raisonEcriture(e: Ecriture): string | null {
  if (e.possible) return null
  if (e.message) return e.message
  if (e.cause === 'GEL') return `Les modifications depuis Gestion sont gelées. ${PHRASE_GEL}`
  if (e.cause === 'MODE_SUR') return `La liaison avec ${NOM_BILLETTERIE} est coupée dans Gestion (Mode sûr).`
  if (e.cause === 'NON_RACCORDEE') return `${NOM_BILLETTERIE} n’est pas encore raccordée : adresse ou jeton à poser dans Administration › Intégrations › Billetterie.`
  return 'Les modifications ne sont pas possibles pour le moment.'
}

export interface EtatBouton { actif: boolean; raison: string | null }

/** §6.5 : actif si les trois le permettent ; sinon la PREMIÈRE raison — droits, écriture, gestes. */
export function etatBouton(nom: NomGeste, geste: Geste, a: Actions, e: Ecriture): EtatBouton {
  const droit = droitManquant(nom, geste, a)
  if (droit) return { actif: false, raison: droit }
  const ecr = raisonEcriture(e)
  if (ecr) return { actif: false, raison: ecr }
  if (!geste.possible) return { actif: false, raison: geste.raison ?? 'La billetterie refuserait ce geste maintenant.' }
  return { actif: true, raison: null }
}

/** Une personne sans aucun droit d'écriture lit la fiche sans boutons (« Lecture seule », design system §8). */
export function lectureSeule(a: Actions): boolean {
  return !a.preparer && !a.publier
}

/** Le geste demandera-t-il une confirmation d'identité ? (dit AVANT le clic) */
export function demandeIdentite(nom: NomGeste, geste: Geste): boolean {
  if (GESTES_DE_VENTE.includes(nom)) return true
  return PREPARATION_EN_LIGNE.includes(nom) && geste.publier_requis
}

/* ═══════════════════════ phrases avant le clic ═══════════════════════ */

function prixEntreParentheses(e: Effet): string {
  return e.prix_cents === null ? '' : ` (${euros(e.prix_cents)})`
}

function effetEnMots(e: Effet): string {
  if (e.apres === 'OUVERT') return `met ${e.nom}${prixEntreParentheses(e)} en vente tout de suite`
  if (e.avant === 'OUVERT') return `arrête la vente de ${e.nom}${prixEntreParentheses(e)} (${(RAISON_DISPO_LIBELLE[e.apres] ?? e.apres).toLowerCase()})`
  return `fait passer ${e.nom} de « ${RAISON_DISPO_LIBELLE[e.avant] ?? e.avant} » à « ${RAISON_DISPO_LIBELLE[e.apres] ?? e.apres} »`
}

/**
 * Phrase imposée (§4.3) : « Retirer SUR PLACE AVANT 2H30 met SUR PLACE
 * (10,00 €) en vente tout de suite. » Sans effet sur les autres tarifs : `null`.
 */
export function phraseEffets(verbe: 'Retirer' | 'Remettre' | 'Supprimer', nom: string, effets: readonly Effet[]): string | null {
  if (effets.length === 0) return null
  return `${verbe} ${nom} ${enumeration(effets.map(effetEnMots))}.`
}

function enumeration(morceaux: readonly string[]): string {
  return morceaux.length === 1 ? morceaux[0]! : `${morceaux.slice(0, -1).join(', ')} et ${morceaux[morceaux.length - 1]!}`
}

/**
 * APRÈS le clic : ce que le geste a VRAIMENT changé aux autres tarifs, d'après
 * la réponse de la billetterie (un tarif ajouté de rang inférieur referme
 * celui qui était ouvert, §4.9). Sans effet : `null`.
 */
export function phraseEffetsApres(effets: readonly Effet[]): string | null {
  if (effets.length === 0) return null
  return `Effet sur les autres tarifs : cela ${enumeration(effets.map(effetEnMots))}.`
}

/**
 * Les notes qui restent sur la fiche après un geste réussi, jusqu'au geste
 * suivant : les avertissements de la billetterie (`HEURE_DECALEE_AMBIGUE` :
 * « l'heure d'hiver a été retenue »), les tarifs décalés avec la nuit, les
 * effets réels sur les autres tarifs. Dites même quand la Save Bar a déjà dit
 * « enregistré » : un toast qui disparaît ne suffit pas.
 */
export function notesApresGeste(r: ResultatGeste | null): string[] {
  if (!r) return []
  const out = r.avertissements.map((a) => a.message)
  if (r.tarifs_decales > 0) {
    out.push(r.tarifs_decales === 1
      ? '1 tarif décalé avec la nuit : ses heures de vente suivent la nouvelle date.'
      : `${r.tarifs_decales} tarifs décalés avec la nuit : leurs heures de vente suivent la nouvelle date.`)
  }
  const effets = phraseEffetsApres(r.effets)
  if (effets) out.push(effets)
  return [...new Set(out)]
}

/** Le message d'« Arrêter les ventes », d'après ce qui a vraiment été retiré (§4.8). */
export function succesArret(r: ResultatGeste | null): string {
  if (r?.inchange) return 'Rien à arrêter : aucun tarif actif dans ces canaux. Rien n’a changé.'
  const n = r?.tarifs_retires.length ?? 0
  return n > 0
    ? `Ventes arrêtées : ${n} tarif${n > 1 ? 's' : ''} retiré${n > 1 ? 's' : ''} de la vente. Les billets vendus entrent toujours.`
    : 'Ventes arrêtées. Les billets vendus entrent toujours.'
}

/**
 * §4.9 : ce que fait un tarif ajouté sur une soirée EN LIGNE, selon son état
 * et le canal choisi. Seul un tarif sur place, sur une soirée en vente, se
 * vend tout de suite à la caisse ; sur une soirée programmée, rien ne se vend
 * avant la mise en vente ; un tarif en ligne ou une invitation ne se vend nulle
 * part pendant la démo (l'avertissement du canal le dit).
 */
export function phraseAjoutEnLigne(s: Soiree, canal: Canal): string {
  const identite = 'Gestion vous demandera de confirmer votre identité.'
  if (canal !== 'SUR_PLACE') return `Cette soirée est en ligne : le tarif s’ajoute tout de suite à la soirée. ${identite}`
  if (s.statut === 'PROGRAMMEE') {
    const quand = s.mise_en_vente_paris ? parisLisible(s.mise_en_vente_paris) : s.mise_en_vente_a ? instantParis(s.mise_en_vente_a) : null
    return `Cette soirée est programmée : ce tarif se vendra à la caisse de la page de secours dès la mise en vente${quand ? `, le ${quand}` : ''}, si ses propres conditions le permettent. ${identite}`
  }
  return `Cette soirée est en ligne : un tarif ajouté se vend tout de suite à la caisse de la page de secours, si ses propres conditions le permettent (les téléphones LC Porte ne le reçoivent qu’à partir de la nuit). ${identite}`
}

/** §4.9 : avant d'ajouter un tarif dans une cascade d'une soirée en vente. */
export function phraseCascadeAjout(statut: StatutSoiree, groupe: string | null, rang: number | null): string | null {
  if (statut !== 'EN_VENTE' || !groupe || rang === null) return null
  return `Ce tarif entre dans la cascade « ${groupe} » au rang ${rang} : il peut fermer le tarif actuellement en vente.`
}

/**
 * §5.1.2 : ce que la caisse vend dès la mise en vente — d'après les tarifs SUR
 * PLACE actifs et LEUR disponibilité propre (`dispo`, calculée par la
 * billetterie sans regarder l'état de la soirée ; `vente_reelle` vaudrait
 * « Brouillon » ici). Un tarif sur place qui ne commence qu'à l'ouverture (les
 * modèles, décision du 22/10) ne se vend pas « tout de suite ».
 */
export function phraseCaisseALaMiseEnVente(s: Soiree): string {
  const surPlace = s.tarifs.filter((t) => t.actif && t.canal === 'SUR_PLACE')
  if (surPlace.length === 0) return 'Rien ne se vendra à la caisse : aucun tarif sur place n’est actif.'
  // Une disponibilité illisible : sans début de vente, on suppose qu'il se vend (le pire, dit).
  if (surPlace.some((t) => t.dispo.raison === 'OUVERT' || (t.dispo.raison === '' && !t.vente_debut))) {
    return 'Elle se vend tout de suite à la caisse de la page de secours, quelle que soit sa nuit.'
  }
  const aVenir = surPlace.filter((t) => t.dispo.raison === 'PAS_ENCORE' && t.vente_debut)
    .sort((a, b) => (a.vente_debut! < b.vente_debut! ? -1 : a.vente_debut! > b.vente_debut! ? 1 : 0))
  const premier = aVenir[0]
  if (premier) {
    const quand = premier.vente_debut_paris ? parisLisible(premier.vente_debut_paris) : instantParis(premier.vente_debut)
    return `Elle sera en vente, mais aucun tarif sur place ne se vend avant le ${quand} : d’ici là, la caisse de la page de secours ne vend rien.`
  }
  return 'Elle sera en vente, mais aucun tarif sur place ne se vend pour l’instant (vente terminée, épuisé ou en attente du tarif précédent).'
}

const PHRASE_PREMIER_BILLET = 'Dès le premier billet vendu : la nuit ne change plus, aucune salle ne se retire, la soirée ne revient plus en brouillon et ne s’annule plus (le remboursement arrive au lot 5).'

/**
 * Ce que le geste va faire, et ce qui deviendra IMPOSSIBLE ensuite — dit dans
 * la confirmation, avant le clic (juge §2, contrat §5). Les avertissements de
 * la billetterie (`RIEN_A_VENDRE`…) suivent.
 */
export function consequences(nom: 'publier' | 'programmer' | 'brouillon' | 'annuler', s: Soiree, geste: Geste): string[] {
  const out: string[] = []
  const fermeture = s.fermeture_heure ? ` (${s.fermeture_heure})` : ''
  if (nom === 'publier') {
    out.push(`${phraseCaisseALaMiseEnVente(s)} Les téléphones LC Porte ne la reçoivent qu’à partir de sa nuit (bascule à 8 h).`)
    out.push(PHRASE_PREMIER_BILLET)
    out.push(`Elle se clôturera seule 2 h après sa fermeture${fermeture}, définitivement.`)
  } else if (nom === 'programmer') {
    out.push('La billetterie la met en vente seule, dans les 30 secondes qui suivent l’heure choisie, même si Gestion est gelé ou en panne. Elle ne revérifie pas les tarifs à ce moment-là.')
    out.push('Pour empêcher la mise en vente, il faudra remettre la soirée en brouillon avant l’heure. Une soirée programmée ne change plus de nuit.')
    out.push(PHRASE_PREMIER_BILLET)
  } else if (nom === 'brouillon') {
    out.push('Plus rien ne se vend : la caisse de la page de secours et les téléphones LC Porte ne la proposent plus. Elle pourra être remise en vente.')
    if (s.statut === 'PROGRAMMEE') out.push('La mise en vente programmée est abandonnée.')
  } else {
    out.push('C’est définitif : rien ne « désannule » une soirée, et une soirée annulée ne se modifie plus. Rien n’est supprimé : elle reste visible, annulée.')
  }
  for (const a of geste.avertissements) out.push(a.message)
  if ((nom === 'publier' || nom === 'programmer') && s.tarifs.some((t) => t.avertissements.some((a) => a.code === 'SUR_PLACE_SANS_DEBUT'))) {
    out.push('Un tarif sur place sans début de vente se vend dès la mise en vente, même des jours avant, à la caisse de la page de secours ; chaque vente y compte aussitôt comme une entrée.')
  }
  return [...new Set(out)]
}

/**
 * Sous le champ Nuit (§4.6, §5.1.7) : pourquoi la date ne se change pas, ou
 * `null` si elle se change. La billetterie pose le verrou NUIT dès un billet
 * vendu OU sur une soirée programmée : la cause se dit dans cet ordre, jamais
 * « un billet est déjà vendu » quand aucun ne l'est.
 */
export function raisonNuitFigee(s: Soiree): string | null {
  if (s.statut === 'PROGRAMMEE') return 'Mise en vente programmée : remettez d’abord la soirée en brouillon pour changer de nuit, puis reprogrammez la vente.'
  if (!s.gestes.modifier.verrous.includes('NUIT')) return null
  return s.compteurs.vendus > 0 ? VERROU_LIBELLE['NUIT']! : 'La date ne se change pas pour le moment.'
}

/**
 * La mise en vente, dans l'en-tête de la fiche : la billetterie pose
 * `publiee_a` à la mise en vente et ne l'efface jamais. « En vente depuis »
 * seulement pour une soirée en vente ; au passé une fois clôturée ou annulée ;
 * rien pour une soirée en brouillon ou programmée (la ligne « programmée le » suffit).
 */
export function faitMiseEnVente(s: Soiree): { libelle: string; valeur: string } | null {
  if (!s.publiee_a) return null
  if (s.statut === 'EN_VENTE') return { libelle: 'En vente depuis', valeur: `le ${instantParis(s.publiee_a)}` }
  if (s.statut === 'CLOTUREE' || s.statut === 'ANNULEE') return { libelle: 'Mise en vente', valeur: `le ${instantParis(s.publiee_a)}` }
  return null
}

export interface ChiffreBandeau { libelle: string; valeur: string; aide: string }

const nombreFr = (n: number): string => n.toLocaleString('fr-FR')

/**
 * Les quatre tuiles du bandeau de la fiche, celui de la fiche d'une nuit en
 * comptabilité, rangées comme le tableau de bord d'un événement Shotgun
 * (23/10/2026) : billets vendus, valeur des billets, entrées, places
 * restantes. La mise en vente se lit dans la colonne de droite. Une valeur
 * que la billetterie n'a pas rendue (ou réservée à un droit) s'écrit « — »,
 * jamais 0 ; la valeur faciale n'est jamais un chiffre d'affaires (§3.5).
 */
export function chiffresDuBandeau(s: Soiree, recetteMasquee: boolean): ChiffreBandeau[] {
  const c = s.compteurs
  const jauge = nombreFr(s.jauge_totale)
  const r = s.recette
  return [
    { libelle: 'Billets vendus', valeur: nombreFr(c.vendus), aide: `Sur une jauge de ${jauge}` },
    {
      libelle: 'Valeur des billets',
      valeur: r ? euros(r.total_cents) : '—',
      aide: r ? 'Frais exclus · pas un chiffre d’affaires'
        : recetteMasquee ? 'Réservée au droit « Consulter la billetterie en direct »' : 'La billetterie ne l’a pas rendue',
    },
    {
      libelle: 'Entrées', valeur: nombreFr(c.entres),
      aide: c.entres === 0 ? 'Personne n’est encore entré' : c.vendus > 0 ? `${Math.round(part(c.entres, c.vendus) * 100)} % des billets vendus` : 'Billets passés à l’entrée',
    },
    {
      libelle: 'Places restantes',
      valeur: c.jauge_restante === null ? '—' : nombreFr(c.jauge_restante),
      aide: s.complet_a ? `Annoncée complet le ${instantParis(s.complet_a)}`
        : `${nombreFr(c.places_occupees)} place${c.places_occupees > 1 ? 's' : ''} prise${c.places_occupees > 1 ? 's' : ''} sur ${jauge}`,
    },
  ]
}

/**
 * Le compte à rebours d'une soirée, comme chez Shotgun : « Ce soir » pour la
 * nuit en cours, « J-6 » avant, « Passée » après. Arithmétique de calendrier
 * seulement, sur la nuit commerciale du jour.
 */
export function compteARebours(nuit: string, nuitDuJour: string): { texte: string; proche: boolean } {
  const a = jourCivil(nuit); const b = jourCivil(nuitDuJour)
  if (!a || !b) return { texte: '—', proche: false }
  const j = Math.round((a.getTime() - b.getTime()) / 86_400_000)
  if (j === 0) return { texte: 'Ce soir', proche: true }
  if (j < 0) return { texte: 'Passée', proche: false }
  return { texte: `J-${j}`, proche: j <= 7 }
}

export interface GroupeTarifs { canal: Canal; titre: string; aide: string; tarifs: Tarif[] }

const GROUPE_CANAL: Record<Canal, { titre: string; aide: string }> = {
  EN_LIGNE: { titre: 'En ligne', aide: 'Sur la page de la soirée' },
  SUR_PLACE: { titre: 'Sur place', aide: 'Vendus à la caisse, le soir même' },
  INVITATION: { titre: 'Invitations', aide: 'Gratuites ; aucun écran ne les émet encore' },
}
const CANAUX_DE_LA_LISTE: readonly Canal[] = ['EN_LIGNE', 'SUR_PLACE', 'INVITATION']

/**
 * Les tarifs rangés comme chez Shotgun (23/10/2026) : par canal — en ligne,
 * sur place, invitations —, dans l'ordre de la billetterie ; les tarifs d'une
 * même cascade se suivent, par rang, à la place du premier d'entre eux.
 */
export function tarifsParCanal(tarifs: readonly Tarif[]): GroupeTarifs[] {
  return CANAUX_DE_LA_LISTE.map((canal) => {
    const du = tarifs.filter((t) => t.canal === canal)
    const ranges: Tarif[] = []
    const vus = new Set<string>()
    for (const t of du) {
      if (vus.has(t.id)) continue
      const suite = t.cascade_groupe
        ? du.filter((x) => x.cascade_groupe === t.cascade_groupe).sort((a, b) => (a.cascade_rang ?? 0) - (b.cascade_rang ?? 0))
        : [t]
      for (const x of suite) { ranges.push(x); vus.add(x.id) }
    }
    return { canal, ...GROUPE_CANAL[canal], tarifs: ranges }
  }).filter((g) => g.tarifs.length > 0)
}

/** Le tarif de la même cascade qui passe juste avant celui-ci, s'il y en a un. */
export function tarifPrecedent(t: Tarif, tarifs: readonly Tarif[]): Tarif | null {
  if (!t.cascade_groupe || t.cascade_rang === null) return null
  const avant = tarifs.filter((x) => x.id !== t.id && x.cascade_groupe === t.cascade_groupe && x.cascade_rang !== null && x.cascade_rang < t.cascade_rang!)
  return avant.sort((a, b) => b.cascade_rang! - a.cascade_rang!)[0] ?? null
}

/** Combien de tarifs se vendent vraiment en ce moment (la tuile « En vente »). */
export function tarifsOuverts(tarifs: readonly Tarif[]): number {
  return tarifs.filter((t) => t.vente_reelle.code === 'OUVERT').length
}

/** « 28/10 à 23:55 » : une heure de vente sans l'année (la saison reste dite quand elle compte). */
export function heureDeVente(paris: string | null, instant: string | null): string {
  return paris ? parisLisible(paris).replace(/^(\d{2}\/\d{2})\/\d{4}/, '$1') : instantParis(instant)
}

/**
 * La ligne sous le nom d'un tarif : quand il se vend, jusqu'à quand on entre,
 * pour quelles salles. Ce qui ne s'applique pas ne s'écrit pas ; un tarif qui
 * suit un autre dans sa cascade ne dit pas « dès la mise en vente ».
 */
export function resumeLigneTarif(t: Tarif, s: Soiree): string {
  const morceaux: string[] = []
  const debut = t.vente_debut_paris || t.vente_debut
  if (debut) morceaux.push(`Dès le ${heureDeVente(t.vente_debut_paris, t.vente_debut)}`)
  else if (!tarifPrecedent(t, s.tarifs)) morceaux.push('Dès la mise en vente')
  if (t.vente_fin_paris || t.vente_fin) morceaux.push(`jusqu’au ${heureDeVente(t.vente_fin_paris, t.vente_fin)}`)
  if (t.entree_avant_heure) morceaux.push(`entrée avant ${heureDeNuitLisible(s.nuit, t.entree_avant_heure)}`)
  if (t.salles && t.salles.length > 0) morceaux.push(t.salles.map((c) => s.salles.find((x) => x.code === c)?.nom ?? c).join(', '))
  if (t.cache) morceaux.push('caché')
  if (!t.actif) morceaux.push('retiré de la vente')
  const texte = morceaux.join(' · ')
  return texte.charAt(0).toUpperCase() + texte.slice(1)
}

/**
 * La liste rangée comme celle des événements chez Shotgun (23/10/2026) :
 * « Ce soir » d'abord, puis par état — en vente, programmées, brouillons,
 * clôturées, annulées —, chacune dans l'ordre de la billetterie. Une
 * recherche garde les soirées dont le nom contient les mots tapés (sans
 * tenir compte des accents ni des majuscules).
 */
export function sectionsDeLaListe(soirees: readonly SoireeListe[], nuitDuJour: string, recherche = ''): { titre: string; soirees: SoireeListe[] }[] {
  const plat = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const mots = plat(recherche).split(/\s+/).filter(Boolean)
  const gardees = soirees.filter((x) => mots.every((m) => plat(x.nom).includes(m)))
  const sections: { titre: string; garde: (x: SoireeListe) => boolean }[] = [
    { titre: 'Ce soir', garde: (x) => x.nuit === nuitDuJour && x.statut !== 'ANNULEE' },
    { titre: 'En vente', garde: (x) => x.statut === 'EN_VENTE' },
    { titre: 'Programmées', garde: (x) => x.statut === 'PROGRAMMEE' },
    { titre: 'Brouillons', garde: (x) => x.statut === 'BROUILLON' },
    { titre: 'Clôturées', garde: (x) => x.statut === 'CLOTUREE' },
    { titre: 'Annulées', garde: (x) => x.statut === 'ANNULEE' },
  ]
  const placees = new Set<string>()
  return sections.map((sec) => {
    const dedans = gardees.filter((x) => !placees.has(x.id) && sec.garde(x))
    for (const x of dedans) placees.add(x.id)
    return { titre: sec.titre, soirees: dedans }
  }).filter((sec) => sec.soirees.length > 0)
}

/** La part d'un total, bornée entre 0 et 1 ; 0 quand le total est nul (rien ne se remplit sur rien). */
export function part(valeur: number, total: number): number {
  return total > 0 ? Math.min(1, Math.max(0, valeur / total)) : 0
}

const CANAL_PLURIEL: Record<Canal, string> = { EN_LIGNE: 'En ligne', SUR_PLACE: 'Sur place', INVITATION: 'Invitations' }

/**
 * Les billets vendus et entrés par canal, sommés sur les tarifs, pour les
 * seuls canaux qui ont un tarif — le panneau « Remplissage » de la fiche.
 */
export function ventesParCanal(s: Soiree): { canal: Canal; libelle: string; vendus: number; entres: number }[] {
  return CANAUX.filter((c) => s.tarifs.some((t) => t.canal === c)).map((c) => {
    const ts = s.tarifs.filter((t) => t.canal === c)
    return {
      canal: c, libelle: CANAL_PLURIEL[c],
      vendus: ts.reduce((n, t) => n + t.compteurs.vendus, 0),
      entres: ts.reduce((n, t) => n + t.compteurs.entres, 0),
    }
  })
}

/** La valeur faciale par moyen de paiement, et sa part du total (§3.5 : jamais un chiffre d'affaires). */
export function partsRecette(r: Recette): { libelle: string; cents: number; part: number }[] {
  return ([['Espèces', r.especes_cents], ['Carte', r.cb_cents], ['En ligne', r.en_ligne_cents]] as const)
    .map(([libelle, cents]) => ({ libelle, cents, part: part(cents, r.total_cents) }))
}

/** La tuile « Mise en vente » : où en est la vente, et depuis quand (le bandeau, et l'état du panneau de mise en vente). */
export function tuileMiseEnVente(s: Soiree): ChiffreBandeau {
  const libelle = 'Mise en vente'
  const fait = faitMiseEnVente(s)
  switch (s.statut) {
    case 'BROUILLON': return { libelle, valeur: 'Pas encore', aide: 'Brouillon : rien n’est en vente' }
    case 'PROGRAMMEE': {
      const quand = parisLisible(s.mise_en_vente_paris) !== '—' ? parisLisible(s.mise_en_vente_paris) : instantParis(s.mise_en_vente_a)
      return { libelle, valeur: 'Programmée', aide: `Ouvrira seule le ${quand}` }
    }
    case 'EN_VENTE': return { libelle, valeur: 'En vente', aide: fait ? `Depuis ${fait.valeur}` : 'Les billets sont en vente' }
    case 'CLOTUREE': return { libelle, valeur: 'Clôturée', aide: fait ? `Mise en vente ${fait.valeur}` : 'La vente est terminée' }
    case 'ANNULEE': return { libelle, valeur: 'Annulée', aide: `${s.annulee_a ? `Le ${instantParis(s.annulee_a)}` : 'Soirée annulée'}${s.motif_annulation ? ` : « ${s.motif_annulation} »` : ''}` }
  }
}

/**
 * Les raisons des verrous d'un tarif quand celle du tableau serait fausse
 * (§4.3) : la billetterie fige aussi le CANAL d'un tarif sur place SANS billet
 * tant qu'une caisse de téléphone est ouverte, et SALLES_RETRAIT compte les
 * billets ÉMIS (annulés compris), pas les vendus.
 */
export function raisonsVerrousTarif(t: Tarif, s: Soiree): Record<string, string> {
  const out: Record<string, string> = {}
  const verrous = t.gestes.modifier.verrous
  const telephone = s.caisses_ouvertes.find((c) => c.telephone)
  if (verrous.includes('CANAL') && t.compteurs.emis === 0 && telephone) {
    out['CANAL'] = `La caisse « ${telephone.poste} » est ouverte sur un téléphone : fermez-la d’abord pour changer le canal.`
  }
  if (verrous.includes('SALLES_RETRAIT')) out['SALLES_RETRAIT'] = 'Aucune salle ne se retire plus : un billet a déjà été émis sur ce tarif. On peut en ajouter.'
  return out
}

/** Ce qui deviendra impossible sur un tarif, dit tant qu'aucun billet n'est émis (§5.2). */
export const PHRASE_PREMIER_BILLET_TARIF = 'Après le premier billet émis sur ce tarif : prix, frais, canal, exonération et TVA seront figés ; l’heure limite d’entrée pourra être repoussée, jamais avancée ; le tarif ne pourra plus être supprimé, seulement retiré de la vente.'

/** Retirer, remettre, supprimer : ce que fait le geste, avant ses effets sur les autres tarifs. */
export function phraseGesteTarif(geste: 'retirer' | 'remettre' | 'supprimer', t: Tarif, statut: StatutSoiree): string {
  const enVente = statut === 'EN_VENTE'
  if (geste === 'retirer') {
    return enVente
      ? 'Il ne se vend plus, ni à la caisse de la page de secours ni sur les téléphones LC Porte. Les billets déjà vendus entrent toujours ; il pourra être remis en vente.'
      : 'Il ne se vendra pas à la mise en vente. Il pourra être remis en vente.'
  }
  if (geste === 'remettre') {
    return enVente
      ? `${t.nom} se vend de nouveau, tout de suite, si ses propres conditions le permettent (début et fin de vente, quota, cascade).`
      : `${t.nom} se vendra de nouveau à la mise en vente.`
  }
  return 'C’est définitif : le tarif disparaît de la soirée. Ce n’est possible que tant qu’aucun billet n’a été émis ; sinon, on le retire de la vente.'
}

/** Les tarifs ACTIFS des canaux choisis : ce qu'« Arrêter les ventes » va retirer. */
export function tarifsArretes(s: Soiree, canaux: readonly Canal[]): Tarif[] {
  return s.tarifs.filter((t) => t.actif && canaux.includes(t.canal))
}

/** §5.1.11 : dit avant le clic, en fonction des canaux cochés. */
export function consequencesArret(s: Soiree, canaux: readonly Canal[]): string[] {
  const out: string[] = []
  const touches = tarifsArretes(s, canaux)
  out.push(touches.length === 0
    ? 'Aucun tarif actif dans ces canaux : rien ne changera.'
    : `${touches.length} tarif${touches.length > 1 ? 's' : ''} retiré${touches.length > 1 ? 's' : ''} de la vente : ${touches.map((t) => t.nom).join(', ')}.`)
  if (canaux.includes('SUR_PLACE')) {
    out.push('La caisse de la porte ne vend plus, téléphones LC Porte compris. Un téléphone hors réseau peut encore vendre jusqu’à sa prochaine synchronisation : ses ventes sont gardées et signalées.')
  }
  out.push('Ce n’est pas une annulation : la soirée reste en vente, les billets vendus entrent toujours, et on reprend tarif par tarif (« Remettre en vente »).')
  return out
}

/* ═══════════════════════ heures ambiguës (§3.4) ═══════════════════════ */

/** « 02:30 (été) » / « 02:30 (hiver) » pour un instant exact proposé par la billetterie. */
export function libelleChoixHeure(possible: string): string {
  const m = /T(\d{2}:\d{2})(?::\d{2}(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.exec(possible)
  if (!m) return possible
  const saison = m[2] === '+02:00' ? 'été' : m[2] === '+01:00' ? 'hiver' : null
  return saison ? `${m[1]} (${saison})` : `${m[1]} (${m[2]})`
}

const NOM_CHAMP: Record<string, string> = {
  vente_debut: 'début de vente', vente_fin: 'fin de vente', entree_avant: 'heure limite d’entrée', a: 'mise en vente',
  ouverture: 'ouverture', fermeture: 'fermeture',
}

/** « Fin de vente du tarif 3 (« SUR PLACE AVANT 2H30 ») » pour `tarifs.2.vente_fin`. */
export function champLisible(champ: string, nomsTarifs: readonly string[] = []): string {
  const m = /^tarifs\.(\d+)\.([a-z_]+)$/.exec(champ)
  if (m) {
    const i = Number(m[1])
    const nom = nomsTarifs[i]
    const quoi = NOM_CHAMP[m[2]!] ?? m[2]!
    return `${quoi[0]!.toUpperCase()}${quoi.slice(1)} du tarif ${i + 1}${nom ? ` (« ${nom} »)` : ''}`
  }
  const base = champ.replace(/^soiree\./, '')
  const quoi = NOM_CHAMP[base] ?? base
  return `${quoi[0]!.toUpperCase()}${quoi.slice(1)}`
}

/**
 * Pose la valeur choisie à son chemin (`tarifs.2.vente_fin`, `a`, `soiree.x`)
 * dans une COPIE du corps : la billetterie reçoit l'instant exact, tel quel.
 */
export function avecValeur<T>(corps: T, chemin: string, valeur: unknown): T {
  const copie = JSON.parse(JSON.stringify(corps)) as unknown
  const parts = chemin.split('.')
  let cur: unknown = copie
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]!
    const next = Array.isArray(cur) ? (cur as unknown[])[Number(k)] : obj(cur)?.[k]
    if (typeof next !== 'object' || next === null) return copie as T
    cur = next
  }
  const dernier = parts[parts.length - 1]!
  if (Array.isArray(cur)) (cur as unknown[])[Number(dernier)] = valeur
  else if (obj(cur)) (cur as Obj)[dernier] = valeur
  return copie as T
}

/* ═══════════════════════ clés d'idempotence (§3.7) ═══════════════════════ */

export interface Stockage {
  getItem(cle: string): string | null
  setItem(cle: string, valeur: string): void
  removeItem(cle: string): void
}

/** Pour les gestes d'une fiche : la clé vit le temps de l'écran. */
export function stockageMemoire(): Stockage {
  const m = new Map<string, string>()
  return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => { m.set(k, v) }, removeItem: (k) => { m.delete(k) } }
}

/** `sessionStorage`, ou la mémoire quand le navigateur le refuse (navigation privée stricte). */
export function stockageSession(): Stockage {
  try {
    const s = globalThis.sessionStorage
    const essai = '__bs_essai__'
    s.setItem(essai, '1'); s.removeItem(essai)
    return s
  } catch {
    return stockageMemoire()
  }
}

export function nouvelleCle(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * UNE CLÉ PAR GESTE, gardée jusqu'à une réponse 2xx LUE (§3.7) :
 *  · réutilisée SEULEMENT si le corps est identique octet pour octet au
 *    précédent envoi — un corps changé (une version relue, une heure choisie)
 *    en tire une neuve, sinon la billetterie répondrait `IDEMPOTENCE_CONFLIT` ;
 *  · rangée par PERSONNE et par ÉCRAN : après une issue incertaine, on recharge
 *    souvent la page, et une clé perdue créerait une deuxième soirée.
 * Le corps comparé est la chaîne même qu'envoie `apiMutate` (`JSON.stringify`).
 */
export class GardeCles {
  readonly #stockage: Stockage
  readonly #prefixe: string
  readonly #generer: () => string

  constructor(stockage: Stockage, personne: string, generer: () => string = nouvelleCle) {
    this.#stockage = stockage
    this.#prefixe = `billetterie-soirees.cle.${personne}.`
    this.#generer = generer
  }

  pour(ecran: string, corps: string): string {
    const k = this.#prefixe + ecran
    try {
      const brut = this.#stockage.getItem(k)
      const lu = brut ? (JSON.parse(brut) as { corps?: unknown; cle?: unknown }) : null
      if (lu && lu.corps === corps && typeof lu.cle === 'string' && /^[A-Za-z0-9_-]{8,128}$/.test(lu.cle)) return lu.cle
    } catch { /* illisible : on repart d'une clé neuve */ }
    const cle = this.#generer()
    try { this.#stockage.setItem(k, JSON.stringify({ corps, cle })) } catch { /* plein : la clé vivra le temps de l'appel */ }
    return cle
  }

  /** Après une réponse 2xx lue : la clé a servi. */
  oublier(ecran: string): void {
    try { this.#stockage.removeItem(this.#prefixe + ecran) } catch { /* rien à oublier */ }
  }
}

/* ═══════════════════════ liste ═══════════════════════ */

export type Periode = 'a-venir' | 'passees' | 'toutes'

/** La query du navigateur, aux clés de la liste blanche (§4.2, §6.1). */
export function requeteListe(o: { periode: Periode; entrainement: boolean; nuitDuJour: string; limite?: number }): Record<string, string> {
  const q: Record<string, string> = { entrainement: o.entrainement ? '1' : '0', limite: String(o.limite ?? 60) }
  if (o.periode === 'a-venir') { q['depuis'] = o.nuitDuJour; q['ordre'] = 'croissant' }
  else if (o.periode === 'passees') { q['jusqua'] = plusJours(o.nuitDuJour, -1); q['ordre'] = 'decroissant' }
  else q['ordre'] = 'decroissant'
  return q
}

/**
 * Une liste coupée à la limite : ce que l'on voit, et où sont les autres. Il
 * n'y a pas de filtre de dates ici (seulement « À venir », « Passées »,
 * « Toutes ») : on ne conseille pas d'en choisir un.
 */
export function phraseTronque(periode: Periode, n: number): string {
  return periode === 'a-venir'
    ? `Seules les ${n} soirées les plus proches sont affichées ; les autres restent visibles sur la page de secours de la billetterie.`
    : `Seules les ${n} soirées les plus récentes sont affichées ; les autres restent visibles sur la page de secours de la billetterie.`
}

/** Les filtres de la liste, dans l'adresse : un Retour depuis une fiche les retrouve. Défauts nus, valeurs inconnues écartées. */
export interface FiltresListe { periode: Periode; entrainement: boolean | null }

export function filtresListeDepuisUrl(p: URLSearchParams): FiltresListe {
  const periode = p.get('periode')
  const e = p.get('entrainement')
  return {
    periode: periode === 'passees' || periode === 'toutes' ? periode : 'a-venir',
    entrainement: e === '1' ? true : e === '0' ? false : null,
  }
}

export function filtresListeVersUrl(f: FiltresListe): URLSearchParams {
  const p = new URLSearchParams()
  if (f.periode !== 'a-venir') p.set('periode', f.periode)
  if (f.entrainement !== null) p.set('entrainement', f.entrainement ? '1' : '0')
  return p
}

const normaliserNom = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim().toLowerCase()

/** §4.5 : une soirée non annulée de même nom et de même nuit existe déjà. */
export function doublonsDe(soirees: readonly SoireeListe[], nom: string, nuit: string): SoireeListe[] {
  const n = normaliserNom(nom)
  if (!n) return []
  return soirees.filter((s) => s.nuit === nuit && s.statut !== 'ANNULEE' && normaliserNom(s.nom) === n)
}

export function phraseDoublon(nom: string): string {
  return `Une soirée « ${nom.trim()} » existe déjà pour cette nuit : vérifiez avant de créer.`
}

/** §4.5 : avant `nuit_proposee`, la soirée ne pourra pas être mise en vente. */
export function nuitPassee(nuit: string, nuitProposee: string | null): boolean {
  return nuitProposee !== null && jourCivil(nuit) !== null && nuit < nuitProposee
}

export const PHRASE_NUIT_PASSEE = 'Cette nuit est passée ou se termine : la soirée ne pourra pas être mise en vente.'

/** « 12 / 300 » : vendus face à la jauge (« 12 / — » si la jauge n'est pas lue). */
export function venteSurJauge(vendus: number, jauge: number | null): string {
  return `${vendus.toLocaleString('fr-FR')} / ${jauge === null ? '—' : jauge.toLocaleString('fr-FR')}`
}
